'use client';

import { cn } from '@shared/components/shadcn';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import type { ComponentProps } from 'react';

type NavigationLinkProps = ComponentProps<'li'> & {
  href: string;
  trackName: string;
  activePaths?: string[];
};

function isCurrentPath(pathname: string, href: string): boolean {
  const hrefPath = href.includes('?') ? href.slice(0, href.indexOf('?')) : href;
  if (pathname === hrefPath) return true;
  if (hrefPath === '/') return false;
  return pathname.startsWith(hrefPath + '/');
}

/**
 * Navigation link component - renders a link with a text and a track name for horizontal navigation bar
 * @param href - The href of the link
 * @param trackName - The track name of the link
 * @param className - (optional) The class name of the link
 * @param props - (optional) The props of the link
 * @returns A navigation HTMLListItem component with a NextLink component inside
 */
function NavigationLink({ href, trackName, className, children, activePaths, ...props }: NavigationLinkProps) {
  const pathname = usePathname();
  const isSelected = isCurrentPath(pathname, href) || (activePaths?.some(candidatePath => isCurrentPath(pathname, candidatePath)) ?? false);

  return (
    <li className={cn('first:pl-1 py-1 px-3', className)} {...props}>
      <Link
        className={cn(
          'group relative flex items-center gap-1 text-foreground transition-colors rounded-md outline-offset-[-4px]',
          'px-2 py-1 -mx-2 -my-1',
          "before:absolute before:-inset-x-2 before:-inset-y-1 before:content-['']",
          'hover:bg-container',
          isSelected && 'bg-container'
        )}
        href={href}
        data-track-name={trackName}
        data-selected={isSelected ? 'true' : undefined}
        data-testid={`navigation-menu-${trackName}`}
      >
        <span className="inline-block relative">
          <span aria-hidden className="invisible paragraph-strong flex items-center gap-1 flex-1 min-w-0">
            {children}
          </span>
          <span className={cn('absolute inset-0 flex items-center justify-center gap-1 paragraph', 'group-hover:paragraph-strong group-data-[selected=true]:paragraph-strong')}>
            {children}
          </span>
        </span>
      </Link>
    </li>
  );
}

export { NavigationLink };
